import React, { useEffect, useState, useContext } from 'react';
import { 
  Box, List, ListItem, ListItemButton, Typography, Button, 
  Avatar, Chip, Divider, IconButton
} from '@mui/joy';
import { AuthContext } from '../../context/AuthContext.jsx';
import { supabase } from '../../supabaseClient';
import { useNavigate, useLocation } from 'react-router-dom';

const ClientSideBar = () => {
  const { user } = useContext(AuthContext);
  const [userName, setUserName] = useState('Client User');
  const [userInitials, setUserInitials] = useState('CU');
  const navigate = useNavigate();
  const location = useLocation();
  const [showSidebar, setShowSidebar] = useState(true);
  const [activeSection, setActiveSection] = useState('#permits');

  useEffect(() => {
    if (!user?.id) return;

    const fetchUserName = async () => {
      const { data, error } = await supabase
        .from('users')
        .select('display_name')
        .eq('id', user.id)
        .single();

      if (error) {
        console.error('Error fetching display name:', error);
      }

      if (data?.display_name) {
        setUserName(data.display_name);
        setUserInitials(data.display_name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2));
      } else if (user.email) {
        setUserName(user.email);
        setUserInitials(user.email.substring(0, 2).toUpperCase());
      }
    };

    fetchUserName();
  }, [user?.id]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  const sections = [
    { 
      id: '#permits', 
      label: 'Permits & Records', 
      icon: 'assignment',
      description: 'View permit status and details'
    },
    { 
      id: '#client-submittals', 
      label: 'Submittals', 
      icon: 'upload_file',
      description: 'Track submittal progress' 
    },
    { 
      id: '#documents', 
      label: 'Documents', 
      icon: 'folder',
      description: 'Access project documents'
    },
    { 
      id: '#gis', 
      label: 'GIS Maps', 
      icon: 'map',
      description: 'Interactive project maps'
    }
  ];

  const handleSectionClick = (sectionId) => {
    setActiveSection(sectionId);
    if (location.pathname !== '/client/home') {
      navigate(`/client/home${sectionId}`);
    } else {
      const section = document.querySelector(sectionId);
      if (section) {
        section.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <>
      {!showSidebar && (
        <IconButton
          onClick={() => setShowSidebar(true)}
          sx={{
            position: 'fixed',
            top: 20,
            left: 20,
            zIndex: 1100,
            bgcolor: 'success.500',
            color: 'white',
            '&:hover': {
              bgcolor: 'success.600',
              transform: 'scale(1.05)',
            },
            boxShadow: '0 4px 20px rgba(0, 168, 132, 0.25)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
          size="lg"
        >
          <span className="material-symbols-outlined" style={{ fontSize: '24px', fontFamily: 'Material Symbols Outlined' }}>
            menu
          </span>
        </IconButton>
      )}

      {showSidebar && (
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            alignSelf: 'flex-start',
            height: '100vh',
            width: 280,
            flexShrink: 0,
            background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
            backdropFilter: 'blur(20px)',
            borderRight: '1px solid',
            borderColor: 'success.200',
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'hidden',
            zIndex: 1000,
            boxShadow: '4px 0 20px rgba(0, 168, 132, 0.08)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(135deg, rgba(0, 168, 132, 0.02) 0%, rgba(0, 126, 51, 0.01) 100%)',
              zIndex: -1,
            }
          }}
          className="fade-in"
        >
          {/* Header Section */}
          <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'success.100' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <img 
                src="/fldp_final_color.png" 
                alt="FLDP Logo" 
                style={{ height: 45, display: 'block' }} 
              />
              <IconButton 
                size="sm" 
                variant="soft" 
                onClick={() => setShowSidebar(false)}
                sx={{ 
                  '&:hover': { 
                    bgcolor: 'success.100', 
                    transform: 'rotate(180deg)'
                  },
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)' 
                }}
              >
                <span className="material-symbols-outlined" style={{ fontSize: '18px', fontFamily: 'Material Symbols Outlined' }}>
                  close
                </span>
              </IconButton>
            </Box>
            <Typography 
              level="h3" 
              sx={{ 
                color: 'success.700',
                fontWeight: 600,
                background: 'linear-gradient(135deg, #00A884, #007E33)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Client Portal
            </Typography>
            <Typography level="body-sm" sx={{ color: 'text.secondary', mt: 0.5 }}>
              Project Management Dashboard
            </Typography>
          </Box>

          {/* User Profile Section */}
          <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'success.100' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar 
                sx={{ 
                  bgcolor: 'success.500',
                  color: 'white',
                  fontWeight: 700,
                  width: 48,
                  height: 48,
                  fontSize: '1.25rem',
                  boxShadow: '0 4px 15px rgba(0, 168, 132, 0.25)'
                }}
              >
                {userInitials}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography 
                  level="title-sm" 
                  sx={{ 
                    color: 'success.700',
                    fontWeight: 600,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {userName}
                </Typography>
                <Chip 
                  size="sm" 
                  variant="soft" 
                  color="success"
                  startDecorator={
                    <span className="material-symbols-outlined" style={{ fontSize: '14px', fontFamily: 'Material Symbols Outlined' }}>
                      verified_user
                    </span>
                  }
                  sx={{ mt: 0.5 }}
                >
                  Client User
                </Chip>
              </Box>
            </Box>
          </Box>

          {/* Navigation Section */}
          <Box sx={{ flex: 1, p: 2, overflowY: 'auto' }}>
            <Typography 
              level="body-sm" 
              sx={{ 
                color: 'text.secondary', 
                fontWeight: 600, 
                px: 2, 
                py: 1, 
                textTransform: 'uppercase', 
                letterSpacing: '0.5px',
                fontSize: '0.75rem'
              }}
            >
              Navigation
            </Typography>
            <List sx={{ '--List-nestedInsetStart': '0px', mt: 1 }}>
              {sections.map(({ id, label, icon, description }) => (
                <ListItem key={id} sx={{ mb: 0.5 }}>
                  <ListItemButton
                    onClick={() => handleSectionClick(id)}
                    selected={activeSection === id}
                    sx={{
                      borderRadius: 'lg',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        bgcolor: 'success.50',
                        transform: 'translateX(4px)',
                        boxShadow: '0 4px 15px rgba(0, 168, 132, 0.1)',
                      },
                      '&.Joy-selected': {
                        bgcolor: 'success.100',
                        color: 'success.700',
                        fontWeight: 600,
                        boxShadow: '0 2px 10px rgba(0, 168, 132, 0.15)',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          left: 0,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          width: 4,
                          height: 32,
                          bgcolor: 'success.500',
                          borderRadius: '0 2px 2px 0',
                        }
                      },
                      py: 1.5,
                      px: 2,
                      position: 'relative'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                      <Box 
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 36,
                          height: 36,
                          borderRadius: 'md',
                          bgcolor: activeSection === id ? 'success.200' : 'neutral.100',
                          color: activeSection === id ? 'success.700' : 'neutral.600',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        }}
                      >
                        <span 
                          className="material-symbols-outlined" 
                          style={{ 
                            fontSize: '20px', 
                            fontFamily: 'Material Symbols Outlined'
                          }}
                        >
                          {icon}
                        </span>
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography 
                          level="title-sm" 
                          sx={{ 
                            fontWeight: activeSection === id ? 600 : 500,
                            color: activeSection === id ? 'success.700' : 'text.primary'
                          }}
                        >
                          {label}
                        </Typography>
                        <Typography 
                          level="body-xs" 
                          sx={{ 
                            color: 'text.secondary',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {description}
                        </Typography>
                      </Box>
                    </Box>
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>

          {/* Footer Section */}
          <Box sx={{ p: 3, borderTop: '1px solid', borderColor: 'success.100' }}>
            <Button
              fullWidth
              variant="soft"
              color="danger"
              onClick={handleLogout}
              startDecorator={
                <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                  logout
                </span>
              }
              sx={{
                borderRadius: 'lg',
                py: 1.5,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 15px rgba(220, 53, 69, 0.25)',
                }
              }}
            >
              Sign Out
            </Button>
          </Box>
        </Box>
      )}
    </>
  );
};

export default ClientSideBar;